import { useCallback, useContext } from 'react';
import {
    Table, Badge, Progress, Spinner,
} from 'reactstrap';
import cls from 'classnames';
import { formatCurrency } from '@s20/common/util/number';
import { pluralize } from '@s20/common/util/l10n';
import CommonContext from '../Common';
import { Alert } from './Modal';
import { INTERPUNCT } from './util';

const getProgressBarColor = (percentage, rawPercentage) => {
    if (percentage === 0) return 'bg-warning';
    if (rawPercentage > 100) return 'bg-danger';
    return 'bg-primary';
};

export const CompletionProgressBar = ({
    progress, loading, label, canViewDetails, className,
}) => {
    const context = useContext(CommonContext);
    const currencyCode = context?.tenant?.defaultCurrencyCode || 'USD';

    const showDetailsModal = useCallback(() => {
        if (!progress?.lineItems?.length) return;

        Alert.show({
            title: `${label} Details`,
            size: 'lg',
            body: (
                <>
                    <div className="small text-muted mb-2">
                        {`${pluralize(progress.completedTimesheetCount, 'approved or exported timesheet')} ${INTERPUNCT} ${pluralize(progress.rainDayTimesheetCount, 'rain day timesheet')} ${INTERPUNCT} ${pluralize(progress.rainDayCount, 'rain day')}`}
                    </div>
                    <Table
                        size="sm"
                        className="mb-0"
                        bordered
                    >
                        <thead>
                            <tr>
                                <th>Item</th>
                                <th className="text-right">Estimate</th>
                                <th className="text-right">Used</th>
                                <th className="text-right">$ Used</th>
                                <th className="text-right">Progress</th>
                            </tr>
                        </thead>
                        <tbody>
                            {progress.lineItems.map((item) => (
                                <tr key={item.contractChargeTypeId}>
                                    <td>{item.chargeTypeName}</td>
                                    <td className="text-right">{item.estimateQuantity}</td>
                                    <td className="text-right">{item.usedQuantity}</td>
                                    <td className="text-right">
                                        {item.unitRate != null
                                            ? formatCurrency(item.usedValue, currencyCode)
                                            : '--'}
                                    </td>
                                    <td
                                        className="align-middle"
                                        style={{ minWidth: '10rem' }}
                                    >
                                        <div className="d-flex align-items-center text-nowrap">
                                            <Progress
                                                value={item.percentage}
                                                className="progress-project flex-grow-1 mr-2"
                                                barClassName={`progress-bar-static ${getProgressBarColor(item.percentage, item.rawPercentage)}`}
                                            />
                                            {item.percentage === 0 && (
                                                <Badge color="warning">0%</Badge>
                                            )}
                                            {item.rawPercentage > 100 && (
                                                <Badge color="danger">
                                                    {Math.round(item.rawPercentage)}
                                                    %
                                                </Badge>
                                            )}
                                            {item.percentage > 0 && item.rawPercentage <= 100 && (
                                                <span>
                                                    {Math.round(item.percentage)}
                                                    %
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </>
            ),
        });
    }, [progress, currencyCode, label]);

    if (loading && !progress) {
        return (
            <div className={cls('mb-2 text-center', className)}>
                <Spinner
                    size="sm"
                    color="secondary"
                />
                <span className="small text-muted ml-2">Loading progress...</span>
            </div>
        );
    }

    if (!progress || !progress.lineItems?.length) {
        return null;
    }

    return (
        <div className={cls('mb-2', className)}>
            <div className="d-flex justify-content-between align-items-center mb-1">
                <strong className="small">{label}</strong>
                <span className="small text-muted">
                    {formatCurrency(progress.totalUsedValue, currencyCode)}
                    {' · '}
                    {progress.rawOverallPercentage}
                    % Complete
                </span>
            </div>

            <Progress
                value={progress.overallPercentage}
                className="progress-project"
                barClassName={`progress-bar-static ${getProgressBarColor(
                    progress.overallPercentage,
                    progress.rawOverallPercentage,
                )}`}
            />

            {progress.rainDayCount > 0 && (
                <div className="small text-muted mt-1">
                    {pluralize(progress.rainDayCount, 'Rain Day')}
                </div>
            )}

            {Boolean(canViewDetails) && (
                <span
                    role="button"
                    tabIndex={0}
                    className="text-primary cursor-pointer small d-inline-block mt-1"
                    onClick={showDetailsModal}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') showDetailsModal();
                    }}
                >
                    Show Details
                </span>
            )}
        </div>
    );
};
